import { useEffect } from 'react';
import { PaymentSource } from '../../../../enum/PaymentSource';
import { t } from 'i18next';
import FilterSelectAll from './FilterSelectAll';
import OptionsList from './OptionsList';
import FilterToogleSwitch from './FilterToogleSwitch';
import usePaymentSourceCountMessage from '../../../../hook/usePaymentSourceCountMessage';
import { useAppDispatch } from '../../../../hook/useAppDispatch';
import { fetchGamesFilter } from '../../../../store/slice/games-slice';
import Search from '../../Common/Search';
import { useGamesFilter } from '../../../../hook/useGamesFilter';
import useGamesCountMessage from '../../../../hook/useGamesCountMessage';
import { useOtherFilter } from '../../../../hook/useOtherFilter';
import { otherFiltersList } from '../../../../config/OtherFiltersList';
import { sortByName } from '../../../../util/sort';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { setShouldSumByDevice } from '../../../../store/slice/global-filters';
import usePaymentTypeCountMessage from '../../../../hook/usePaymentTypeCountMessage';
import { Games } from '../../../../enum/Games';
import { PaymentType } from '../../../../enum/PaymentType';
import { PLURALIZED_COLUMNS } from '../../../../constant/constants';

interface OtherFiltersProps {
  toggleDropdown: (type: string, index: number) => void;
  isOpen: (type: string, index: number) => boolean;
  filters: any;
  setFilters: (newFilters: {}) => void;
  justClosed: { type: string; index: number } | null;
}

const OtherFilters: React.FC<OtherFiltersProps> = ({ toggleDropdown, isOpen, filters, setFilters, justClosed }) => {
  const dispatch = useAppDispatch();
  const shouldSumByDevice = useAppSelector((state) => state.globalFilters.shouldSumByDevice);

  useEffect(() => {
    dispatch(fetchGamesFilter({ text: '' }));
  }, [dispatch]);

  const { gamesFilterOptions, filteredData, handleFilteredData } = useGamesFilter();

  const getPaymentSourceCountMessage = usePaymentSourceCountMessage();
  const getGamesCountMessage = useGamesCountMessage();
  const getPaymentTypeCountMessage = usePaymentTypeCountMessage();

  const { selectAll, numberOfSelected, handleChange } = useOtherFilter(filters, setFilters, PLURALIZED_COLUMNS);

  return (
    <div className="filters other_filters">
      {otherFiltersList.map((filterList: any, idx) => {
        const isDropdownOpen = isOpen(filterList.label, idx);

        return (
          <div key={filterList.key} className="filter_select_wrapper">
            <div
              data-pw={`filter${filterList.key}`}
              className={`filter_select
                ${isDropdownOpen ? 'opened' : ''}
                ${justClosed?.type === filterList.label && justClosed?.index === idx ? 'closed' : ''}
                ${numberOfSelected[filterList.key] > 0 ? 'expanded' : ''}
                ${filterList.key}
              `}
              onClick={() => toggleDropdown(filterList.label, idx)}
            >
              {filterList.hasSwitch ? (
                <div className={`payment_source_title ${!isDropdownOpen ? 'with_padding' : ''}`}>
                  <span className="dropdown_title">
                    {numberOfSelected[PaymentSource.PAYMENT_SOURCE] > 0 && (
                      <span className="number_of_selected">{numberOfSelected[PaymentSource.PAYMENT_SOURCE]}</span>
                    )}
                    {getPaymentSourceCountMessage(numberOfSelected[PaymentSource.PAYMENT_SOURCE])}
                    {isDropdownOpen && (
                      <FilterToogleSwitch
                        checked={shouldSumByDevice}
                        toogleSwitch={() => dispatch(setShouldSumByDevice(!shouldSumByDevice))}
                        title={t(filterList.switchLabel)}
                      />
                    )}
                  </span>
                </div>
              ) : filterList.key === Games.GAMES ? (
                <span className={`dropdown_title ${!isDropdownOpen ? 'with_padding' : ''}`}>
                  {numberOfSelected[Games.GAMES] > 0 && (
                    <span className="number_of_selected">{numberOfSelected[Games.GAMES]}</span>
                  )}
                  {getGamesCountMessage(numberOfSelected[Games.GAMES])}
                </span>
              ) : filterList.key === PaymentType.PAYMENT_TYPE ? (
                <span className={`dropdown_title ${!isDropdownOpen ? 'with_padding' : ''}`}>
                  {numberOfSelected[PaymentType.PAYMENT_TYPE] > 0 && (
                    <span className="number_of_selected">{numberOfSelected[PaymentType.PAYMENT_TYPE]}</span>
                  )}
                  {getPaymentTypeCountMessage(numberOfSelected[PaymentType.PAYMENT_TYPE])}
                </span>
              ) : (
                <span className={`dropdown_title ${!isDropdownOpen ? 'with_padding' : ''}`}>{t(filterList.label)}</span>
              )}

              {isDropdownOpen && (
                <div className="options_container" onClick={(e) => e.stopPropagation()}>
                  {filterList.search && (
                    <Search
                      placeholder={t('general.search')}
                      className="games_filter_search"
                      data={gamesFilterOptions}
                      searchKeys={[t('statisticPage.header.filter.dropdown.geoFilters.name')]}
                      onFilteredData={handleFilteredData}
                      autoFocus
                    />
                  )}
                  <div className="options_list">
                    {filterList.type === 'radio' && (
                      <OptionsList
                        isFullHeight={true}
                        column={filterList.key}
                        filters={filters}
                        filteredOptions={sortByName(filterList.options)}
                        checkbox={false}
                      />
                    )}
                    {filterList.type === 'checkbox' && (
                      <>
                        {(filterList.key === Games.GAMES ? filteredData?.length : filterList.options?.length) ? (
                          <>
                            <FilterSelectAll
                              className="select_all_wrapper"
                              checked={
                                filters[filterList.key]?.length ===
                                (filterList.key === Games.GAMES
                                  ? filteredData?.length > 0 && filteredData?.length
                                  : filterList.options?.length)
                              }
                              selectAll={() =>
                                filterList.key === Games.GAMES
                                  ? selectAll(filterList.key, filteredData)
                                  : selectAll(filterList.key, filterList.options)
                              }
                              title={t('statisticPage.header.filter.dropdown.all')}
                            />
                            <OptionsList
                              checkbox={true}
                              column={filterList.key}
                              filters={filters}
                              handleChange={handleChange}
                              filteredOptions={
                                filterList.key === Games.GAMES ? filteredData : sortByName(filterList.options)
                              }
                            />
                          </>
                        ) : (
                          <div className="no_results">{t('general.noResultsFound')}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OtherFilters;
